import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Save, Loader2, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { fetchNotificationSettings, saveNotificationSettings } from '@/lib/data-service';
import type { NotificationSettingsData } from '@/lib/data-service';

const ALL_DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
] as const;

export function SettingsNotificationsPage() {
  const [settings, setSettings] = useState<NotificationSettingsData>({
    projectAssignmentEnabled: true,
    projectOverBudgetEnabled: true,
    billingTriggerDateEnabled: true,
    billingTriggerDateDays: 'mon,tue,wed,thu,fri',
    billingTriggerDateTimeUtc: '06:00',
    fixedPriceBillingPercentEnabled: true,
  });
  const [savedSettings, setSavedSettings] = useState<NotificationSettingsData>({
    projectAssignmentEnabled: true,
    projectOverBudgetEnabled: true,
    billingTriggerDateEnabled: true,
    billingTriggerDateDays: 'mon,tue,wed,thu,fri',
    billingTriggerDateTimeUtc: '06:00',
    fixedPriceBillingPercentEnabled: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const hasUnsavedChanges =
    settings.projectAssignmentEnabled !== savedSettings.projectAssignmentEnabled ||
    settings.projectOverBudgetEnabled !== savedSettings.projectOverBudgetEnabled ||
    settings.billingTriggerDateEnabled !== savedSettings.billingTriggerDateEnabled ||
    settings.billingTriggerDateDays !== savedSettings.billingTriggerDateDays ||
    settings.billingTriggerDateTimeUtc !== savedSettings.billingTriggerDateTimeUtc ||
    settings.fixedPriceBillingPercentEnabled !== savedSettings.fixedPriceBillingPercentEnabled;

  const selectedDays = new Set(settings.billingTriggerDateDays.split(',').filter(Boolean));

  const toggleDay = (day: string) => {
    const next = new Set(selectedDays);
    if (next.has(day)) {
      if (next.size > 1) next.delete(day);
    } else {
      next.add(day);
    }
    const ordered = ALL_DAYS.map(d => d.key).filter(k => next.has(k));
    setSettings(prev => ({ ...prev, billingTriggerDateDays: ordered.join(',') }));
  };

  // Load settings on mount
  useEffect(() => {
    async function loadSettings() {
      try {
        const data = await fetchNotificationSettings();
        setSettings(data);
        setSavedSettings(data);
      } catch (error) {
        console.error('Failed to load notification settings:', error);
        toast.error('Failed to load notification settings.');
      } finally {
        setIsLoading(false);
      }
    }
    loadSettings();
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveNotificationSettings(settings);
      setSavedSettings({ ...settings });
      toast.success('Notification settings saved successfully.');
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      toast.error('Failed to save notification settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [settings]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-6 space-y-6 pb-32">
          {/* Page Header */}
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">Notification Settings</h1>
            <p className="text-muted-foreground">
              Configure how and when users are notified about project-related events
            </p>
          </div>

          {/* Project Notifications Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Project Notifications
              </CardTitle>
              <CardDescription>
                Configure notifications for project events
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Project Assignment Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="project-assignment" className="text-sm font-medium">
                    Project Assignment
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Notify users when they are assigned as Project Manager for a project
                  </p>
                </div>
                <Switch
                  id="project-assignment"
                  checked={settings.projectAssignmentEnabled}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, projectAssignmentEnabled: checked }))
                  }
                />
              </div>

              <Separator />

              {/* Project Over Budget Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="project-over-budget" className="text-sm font-medium">
                    Project Over Budget
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Notify the Project Manager when a project exceeds its budget
                  </p>
                </div>
                <Switch
                  id="project-over-budget"
                  checked={settings.projectOverBudgetEnabled}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, projectOverBudgetEnabled: checked }))
                  }
                />
              </div>

              <Separator />

              {/* Billing Trigger Date Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="billing-trigger-date" className="text-sm font-medium">
                    Billing Trigger Date
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Notify the Project Manager when a billing milestone trigger date has passed and an invoice needs to be generated
                  </p>
                </div>
                <Switch
                  id="billing-trigger-date"
                  checked={settings.billingTriggerDateEnabled}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, billingTriggerDateEnabled: checked }))
                  }
                />
              </div>

              <Separator />

              {/* Fixed Price Billing by % Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="fixed-price-billing-percent" className="text-sm font-medium">
                    Fixed Price Billing by %
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Notify the Project Manager when a fixed price project reaches the billing milestone trigger percentage and an invoice needs to be generated
                  </p>
                </div>
                <Switch
                  id="fixed-price-billing-percent"
                  checked={settings.fixedPriceBillingPercentEnabled}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, fixedPriceBillingPercentEnabled: checked }))
                  }
                />
              </div>

              {/* Schedule controls (only visible when billing trigger date is enabled) */}
              {settings.billingTriggerDateEnabled && (
                <div className="ml-0 pl-4 border-l-2 border-muted space-y-4">
                  {/* Day-of-week picker */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Send on days</Label>
                    <div className="flex flex-wrap gap-2">
                      {ALL_DAYS.map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleDay(key)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                            selectedDays.has(key)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-muted-foreground border-input hover:bg-accent'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Time picker */}
                  <div className="space-y-2">
                    <Label htmlFor="billing-trigger-time" className="text-sm font-medium">Send at time (UTC)</Label>
                    <Input
                      id="billing-trigger-time"
                      type="time"
                      value={settings.billingTriggerDateTimeUtc}
                      onChange={(e) =>
                        setSettings(prev => ({ ...prev, billingTriggerDateTimeUtc: e.target.value }))
                      }
                      className="w-32"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Separator />

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges || isSaving}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsNotificationsPage;
